import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { PushQuoteService } from '@zero/shared/ui-push';
import { StoreErrorHandler } from '@zero/shared/util-global-state';
import { Quote } from '@zero/shared/util-models';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';
import { CryptoBase } from '../entities/crypto-base.model';
import { CryptoInfrastructure } from '../infrastructure/crypto.infrastructure';
import { cryptoActions, cryptoApiActions } from './crypto.actions';
import { cryptoFeature } from './crypto.reducers';
import { selectEtpItems } from './crypto.selectors';

@Injectable({ providedIn: 'root' })
export class CryptoEffects {
  readonly #actions$ = inject(Actions);
  readonly #store = inject(Store);
  readonly #cryptoInfrastructure = inject(CryptoInfrastructure);
  readonly #pushQuoteService = inject(PushQuoteService);
  readonly #storeErrorHandler = inject(StoreErrorHandler);

  public loadCryptos$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(cryptoActions.loadCryptos),
      concatLatestFrom(() => this.#store.select(cryptoFeature.selectCryptosLoadingState)),
      filter(([, loadingState]) => loadingState === 'INITIAL'),
      map(() => cryptoApiActions.loadCryptosFromApi()),
    );
  });

  public loadCryptosFromApi$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(cryptoApiActions.loadCryptosFromApi),
      switchMap(() =>
        this.#cryptoInfrastructure.getCryptos().pipe(
          map((cryptos) => cryptoApiActions.loadCryptosSuccess({ cryptos })),
          catchError((error) => of(cryptoApiActions.loadCryptosFailure({ error }))),
        ),
      ),
    );
  });

  public subscribeToCryptoQuotes$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(cryptoActions.subscribeToCryptoQuotes),
      switchMap(() => this.#store.select(cryptoFeature.selectCryptoIsins)),
      switchMap((isins) =>
        this.#cryptoInfrastructure.getHistoryQuotes(isins).pipe(
          map((historicQuotes) => {
            return { isins, historicQuotes };
          }),
        ),
      ),
      switchMap(({ isins, historicQuotes }) =>
        this.#pushQuoteService.subscribeMultiBuffered(isins).pipe(
          filter((quotes) => !!quotes),
          map((quotes) => cryptoActions.updateCryptoQuotes({ quotes, historicQuotes })),
        ),
      ),
    );
  });

  public calculateCryptosPerformance$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(cryptoActions.updateCryptoQuotes),
      concatLatestFrom(() => this.#store.select(cryptoFeature.selectCryptos)),
      filter(([{ quotes, historicQuotes }, cryptos]) => !!quotes && !!historicQuotes && !!cryptos),
      map(([{ quotes, historicQuotes }, cryptos]) =>
        cryptoActions.updateCryptos({ cryptos: this.calculateCryptosPerformance(cryptos, quotes, historicQuotes) }),
      ),
    );
  });

  public unsubscribeFromCryptoQuotes$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(cryptoActions.unsubscribeFromCryptoQuotes),
        concatLatestFrom(() => this.#store.select(cryptoFeature.selectCryptoIsins)),
        tap(([, isins]) => this.#pushQuoteService.unsubscribeMulti(isins)),
      );
    },
    { dispatch: false },
  );

  public loadETPs$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(cryptoActions.loadETPs),
      concatLatestFrom(() => this.#store.select(cryptoFeature.selectEtpsLoadingState)),
      filter(([, loadingState]) => loadingState === 'INITIAL'),
      map(() => cryptoApiActions.loadETPsFromApi()),
    );
  });

  public loadETPsFromApi$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(cryptoApiActions.loadETPsFromApi),
      switchMap(() =>
        this.#cryptoInfrastructure.getETPs().pipe(
          map((etps) => cryptoApiActions.loadETPsSuccess({ etps })),
          catchError(() => of(cryptoApiActions.loadETPsFailure())),
        ),
      ),
    );
  });

  public $subscribeToETPQuotes = createEffect(() => {
    return this.#actions$.pipe(
      ofType(cryptoActions.subscribeToETPQuotes),
      switchMap(() => this.#store.select(cryptoFeature.selectEtpIsins)),
      switchMap((isins) =>
        this.#cryptoInfrastructure.getHistoryQuotes(isins).pipe(
          map((historicQuotes) => {
            return { isins, historicQuotes };
          }),
        ),
      ),
      switchMap(({ isins, historicQuotes }) =>
        this.#pushQuoteService.subscribeMultiBuffered(isins).pipe(
          filter((quotes) => !!quotes),
          map((quotes) => cryptoActions.updateETPQuotes({ quotes, historicQuotes })),
        ),
      ),
    );
  });

  public calculateETPsPerformance$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(cryptoActions.updateETPQuotes),
      concatLatestFrom(() => this.#store.select(selectEtpItems)),
      filter(([{ quotes, historicQuotes }, etps]) => !!quotes && !!historicQuotes && !!etps),
      map(([{ quotes, historicQuotes }, etps]) => this.calculateCryptosPerformance(etps, quotes, historicQuotes)),
      concatLatestFrom(() => this.#store.select(cryptoFeature.selectEtps)),
      map(([updatedEtps, etpListItems]) => {
        const updatedEtpsMap = new Map(updatedEtps.map((etp) => [etp.isin, etp]));

        const updatedEtpListItems = etpListItems.map((etpListItem) => {
          const updatedEtpsInListItem = etpListItem.etps.map((listEtp) => {
            const updateEtp = updatedEtpsMap.get(listEtp.isin);
            return updateEtp || listEtp;
          });

          return { ...etpListItem, etps: updatedEtpsInListItem };
        });

        return cryptoActions.updateETPs({ etps: updatedEtpListItems });
      }),
    );
  });

  public unsubscribeFromETPQuotes$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(cryptoActions.unsubscribeFromETPQuotes),
        concatLatestFrom(() => this.#store.select(cryptoFeature.selectEtpIsins)),
        tap(([, isins]) => this.#pushQuoteService.unsubscribeMulti(isins)),
      );
    },
    { dispatch: false },
  );

  public handleBackendError$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(cryptoApiActions.loadCryptosFailure),
        tap(({ error }) => this.#storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );

  private calculateCryptosPerformance(
    cryptos: CryptoBase[],
    quotes: Record<string, Quote>,
    historicQuotes: Record<string, Quote>,
  ): CryptoBase[] {
    return cryptos.map((crypto: CryptoBase) => {
      const isin = crypto.isin;
      const quote = quotes[isin];
      const historicQuote = historicQuotes[isin]?.[0];

      if (!quote || !historicQuote) {
        return crypto;
      }

      const absChange = quote.ask - historicQuote.ask;
      const percentChange = historicQuote.ask !== 0 ? (absChange / historicQuote.ask) * 100 : undefined;

      return {
        ...crypto,
        ask: quote.ask,
        bid: quote.bid,
        quoteTime: quote.time,
        abs: absChange,
        percent: percentChange,
      };
    });
  }
}
